import React, { useEffect, useState } from 'react';
import { ArrowLeft, Target } from 'lucide-react';
import api from '../lib/api';
import FeatureHeader from './FeatureHeader';

const GOALS = ['Improve Sleep', 'Lose Weight', 'Build Muscle', 'Reduce Stress', 'Increase Energy'];

export default function HealthGoals({ onBack }) {
  const [form, setForm] = useState({
    primaryGoal: 'Improve Sleep',
    weeklyTarget: 5,
    reminderTime: '08:00',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadGoal = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/health-goals');
      const g = res.data?.goal;
      if (g) {
        setForm({
          primaryGoal: g.primaryGoal || 'Improve Sleep',
          weeklyTarget: Number(g.weeklyTarget) || 5,
          reminderTime: g.reminderTime || '08:00',
        });
      }
    } catch (err) {
      setError('Failed to load your health goals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGoal();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        primaryGoal: form.primaryGoal,
        weeklyTarget: Number(form.weeklyTarget),
        reminderTime: form.reminderTime,
      };
      await api.put('/api/health-goals', payload);
      setMessage('Health goals updated successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update goals.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={pageStyle}>
      <FeatureHeader title="Health Goals" onBack={onBack} />

      <div style={motivationCardStyle}>
        <Target size={24} style={{ color: 'var(--primary)' }} />
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: '600' }}>Stay consistent</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>Small steps lead to big wins.</div>
        </div>
      </div>

      {loading ? (
        <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading goals...</div>
      ) : (
        <>
          <label style={labelStyle}>Primary Goal</label>
          <select
            style={inputStyle}
            value={form.primaryGoal}
            onChange={(e) => setForm((prev) => ({ ...prev, primaryGoal: e.target.value }))}
          >
            {GOALS.map((goal) => (
              <option key={goal} value={goal}>{goal}</option>
            ))}
          </select>

          <label style={labelStyle}>Weekly Target (days)</label>
          <input
            type="number"
            min={1}
            max={14}
            value={form.weeklyTarget}
            onChange={(e) => setForm((prev) => ({ ...prev, weeklyTarget: e.target.value }))}
            style={inputStyle}
          />

          <label style={labelStyle}>Preferred Reminder Time</label>
          <input
            type="time"
            value={form.reminderTime}
            onChange={(e) => setForm((prev) => ({ ...prev, reminderTime: e.target.value }))}
            style={inputStyle}
          />

          <button style={buttonStyle} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Update Goals'}
          </button>

          {message && <div style={successStyle}>{message}</div>}
          {error && <div style={errorStyle}>{error}</div>}
        </>
      )}
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginRight: 8,
  cursor: 'pointer',
};

const motivationCardStyle = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 24,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const labelStyle = {
  fontSize: '0.85rem',
  marginTop: 16,
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: 'var(--surface)',
  color: 'var(--on-bg)',
  fontSize: '0.9rem',
};

const buttonStyle = {
  marginTop: 24,
  padding: '10px 16px',
  background: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const successStyle = {
  marginTop: 10,
  color: '#15803d',
  fontSize: '0.85rem',
};

const errorStyle = {
  marginTop: 10,
  color: '#b42318',
  fontSize: '0.85rem',
};
